import { createContext, useContext } from 'react';
import type { Observable } from 'rxjs';
import type { WSSubscriptionMemoryOutput } from '../pipes/wsSubscriptionMemory';
import type { SecurityTypes } from '../types';
import type {
  OTCFixedRateLoanProductTemplate,
  OTCFloatingRateLoanProductTemplate,
  OTCForwardProductTemplate,
  OTCOptionProductTemplate,
  OTCRepoProductTemplate,
} from '../types/OTC/otcSecurityServerTypes';
import type { Security } from '../types/Security';

export const SecuritiesContext = createContext<SecuritiesContextProps | undefined>(undefined);
SecuritiesContext.displayName = 'SecuritiesContext';

export function useSecuritiesContext() {
  const context = useContext(SecuritiesContext);
  if (context === undefined) {
    throw new Error('Missing SecuritiesContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export interface SecuritiesContextProps {
  isLoaded: boolean;
  hasBPSPricingSecurities: boolean;
  securityMemoryObs: Observable<WSSubscriptionMemoryOutput<Security>>;
  securitiesBySymbolObs: Observable<Map<string, Security>>;
  securitiesBySymbol: Map<string, Security>;
  activeSecuritiesBySymbol: Map<string, Security>;
  securitiesList: Security[];
  /**
   * A sorted list of securities based on their `Rank` value.
   */
  securitiesListSortedByRank: Security[];
  predictionOutcomesBySymbol: Map<string, Security>;
  predictionOutcomesByEventID: Map<string, Map<string, Security>>;
  predictionSupportedMarkets: string[];
  /** The searchable securities is a sorted list of all securities by DisplayName, with any recent selections weighted to be sorted to the top as well.
   * Note that this list has securities with EndTime (indicates it was soft deleted) and all expired securities filtered out.
   * This data set is used for the SymbolSelectors in the app, for example.
   */
  searchableSecurities: Security[];
  perps: SecurityTypes['Perp'][];
  perpsByCurrencyPair: Map<string, Map<string, SecurityTypes['Perp']>>;
  cfds: SecurityTypes['CFD'][];
  futures: SecurityTypes['Future'][];
  futuresByCurrencyPair: Map<string, Map<string, SecurityTypes['Future']>>;
  futuresByMarketByCurrency: Map<string, Map<string, Set<SecurityTypes['Future']>>>;

  options: SecurityTypes['Option'][];
  expirationByMarketByCurrencyIdentity: Map<string, Map<string, Set<string>>>;
  optionSecurityBySymbol: Map<string, SecurityTypes['Option']>;
  optionSecurityBySymbolObs: Observable<Map<string, SecurityTypes['Option']>>;
  strikesByOptionKey: Map<string, Map<string, string>>;
  /** A mapping from Currency to all spot trading pairs where the indexed currency is the Base Currency of the trading pair. */
  spotTradingPairsByBaseCurrency: Map<string, Map<string, SecurityTypes['Spot']>>;
  /** A mapping from Currency to all spot trading pairs where the indexed currency is the Quote Currency of the trading pair. */
  spotTradingPairsByQuoteCurrency: Map<string, Map<string, SecurityTypes['Spot']>>;

  /**
   * OTC product templates keyed by DisplaySymbol. For product-builder display-name validation only;
   * use securitiesBySymbol for general template or security lookup.
   */
  otcFixedRateLoanProductTemplatesByDisplaySymbol: Map<string, OTCFixedRateLoanProductTemplate>;
  otcForwardProductTemplatesByDisplaySymbol: Map<string, OTCForwardProductTemplate>;
  otcOptionProductTemplatesByDisplaySymbol: Map<string, OTCOptionProductTemplate>;
  otcFloatingRateLoanProductTemplatesByDisplaySymbol: Map<string, OTCFloatingRateLoanProductTemplate>;
  otcRepoProductTemplatesByDisplaySymbol: Map<string, OTCRepoProductTemplate>;
}
